import { StoreDetailMetadata } from 'components/Basic/Head/StoreDetailMetadata/StoreDetailMetadata';
import Breadcrumbs from 'components/Layout/Breadcrumbs/Breadcrumbs';
import CommonLayout from 'components/Layout/CommonLayout';
import { Webline } from 'components/Layout/Webline/Webline';
import StoreDetail from 'components/Pages/StoreDetail/StoreDetail';
import { mapStoreDetailApiData } from 'connectors/stores/StoreDetail';
import { useDomainConfig } from 'context/DomainConfigProvider/DomainConfigProvider';
import {
    StoreDetailQueryApi,
    StoreDetailQueryDocumentApi,
    StoreDetailQueryVariablesApi,
    useStoreDetailQueryApi,
} from 'graphql/generated';
import { createClient } from 'helpers/createClient';
import { initServerSideProps, ServerSidePropsType } from 'helpers/InitServerSideProps';
import { getIsRedirectedFromSsr } from 'helpers/isServer';
import { getUrlWithoutGetParameters } from 'helpers/parsing/urlParsing';
import { getSeoTitleAndDescriptionForFriendlyUrlPage } from 'helpers/seo/getSeoTitleAndDescriptionForFriendlyUrlPage';
import { getServerSidePropsWrapper } from 'helpers/serverSide/getServerSidePropsWrapper';
import { handleServerSideErrorResponseForFriendlyUrls } from 'helpers/serverSide/handleServerSideErrorResponseForFriendlyUrls';
import { useQueryError } from 'hooks/graphQl/UseQueryError';
import { useGtmFriendlyPageView } from 'hooks/gtm/useGtmFriendlyPageView';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { NextPageExtended } from 'types/global';
import { CombinedError, OperationResult } from 'urql';
import { getInternationalizedStaticUrls } from 'utils/getInternationalizedStaticUrls';
import { getSlugFromServerSideUrl, getSlugFromUrl } from 'utils/getSlugFromUrl';
import { useGtmPageViewEvent } from 'utils/Gtm/EventFactories';
import { getGtmPageInfoForFriendlyUrl } from 'utils/Gtm/Gtm';

const StoreDetailPage: NextPageExtended<ServerSidePropsType> = () => {
    const router = useRouter();
    const { t } = useTranslation();
    const domainConfig = useDomainConfig();
    const slug = getUrlWithoutGetParameters(router.asPath);
    const [{ data: storeDetailData, fetching, error }] = useStoreDetailQueryApi({
        variables: { urlSlug: getSlugFromUrl(slug) },
    });
    useQueryError(error, error instanceof CombinedError);

    const [storeListUrl] = getInternationalizedStaticUrls(['/stores'], domainConfig.url);

    const mappedStore = useMemo(() => {
        if (storeDetailData?.store) {
            return mapStoreDetailApiData(storeDetailData.store);
        }

        return null;
    }, [storeDetailData?.store]);

    const storeSeoTitleAndDescription = mappedStore
        ? getSeoTitleAndDescriptionForFriendlyUrlPage(mappedStore)
        : undefined;

    const gtmStoreUrlPageViewEvent = useGtmPageViewEvent(
        getGtmPageInfoForFriendlyUrl(mappedStore, slug, mappedStore?.breadcrumb),
    );
    useGtmFriendlyPageView(gtmStoreUrlPageViewEvent, slug, fetching);

    return (
        <CommonLayout
            showLastVisitedProducts
            description={storeSeoTitleAndDescription?.description}
            isFetchingData={fetching}
            title={storeSeoTitleAndDescription?.title}
        >
            {mappedStore && (
                <>
                    <StoreDetailMetadata store={mappedStore} />
                    <Webline>
                        <Breadcrumbs
                            key="breadcrumb"
                            breadcrumb={[
                                { name: t('Stores'), slug: storeListUrl },
                                { name: mappedStore.storeName, slug: '' },
                            ]}
                        />
                    </Webline>
                    <StoreDetail fetching={fetching} store={mappedStore} />
                </>
            )}
        </CommonLayout>
    );
};

StoreDetailPage.layout = 'plain';

export const getServerSideProps = getServerSidePropsWrapper(
    ({ domainConfig, redisClient, ssrExchange, t }) =>
        async (context) => {
            const client = createClient({
                publicGraphqlEndpoint: domainConfig.publicGraphqlEndpoint,
                ssrExchange,
                t,
                context,
                redisClient,
            });

            const storeResponse: OperationResult<StoreDetailQueryApi, StoreDetailQueryVariablesApi> = await client
                .query(StoreDetailQueryDocumentApi, {
                    urlSlug: getSlugFromServerSideUrl(context.req.url ?? '', context.req.headers),
                })
                .toPromise();

            if (getIsRedirectedFromSsr(context.req.headers)) {
                const serverSideErrorResponse = handleServerSideErrorResponseForFriendlyUrls(
                    storeResponse.error?.graphQLErrors,
                    storeResponse.data?.store,
                    context.res,
                );

                if (serverSideErrorResponse) {
                    return serverSideErrorResponse;
                }
            }

            const initServerSideData = await initServerSideProps({ context, domainConfig, client, ssrExchange });

            return initServerSideData;
        },
);

export default StoreDetailPage;
